import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ProductCard from '../components/cards/ProductCard';
import EmptyState from '../components/common/EmptyState';
import {
  FiBox,
  FiDownload,
  FiInfo,
  FiX,
  FiFileText,
  FiExternalLink,
} from 'react-icons/fi';

const FinalProduct = () => {
  const { finalProducts } = useOutletContext();
  const [activeTab, setActiveTab] = useState('all');
  const [previewProduct, setPreviewProduct] = useState(null);

  const filteredProducts = finalProducts.filter((product) => {
    if (activeTab === 'all') return true;
    return product.product_type === activeTab;
  });

  const getFileExtension = (url = '') => {
    try {
      const cleanUrl = url.split('?')[0];
      return cleanUrl.split('.').pop().toLowerCase();
    } catch {
      return '';
    }
  };

  const isImageFile = (url = '') => {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(
      getFileExtension(url)
    );
  };

  const isPdfFile = (url = '') => {
    return getFileExtension(url) === 'pdf';
  };

  const isWordFile = (url = '') => {
    return ['doc', 'docx'].includes(getFileExtension(url));
  };

  const handleDownload = (product) => {
    if (!product?.file_url) return;

    const link = document.createElement('a');
    link.href = product.file_url;
    link.download = product.title || 'final-product';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenFile = (product) => {
    if (!product?.file_url) return;

    window.open(
      product.file_url,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const renderPreview = (product) => {
    const fileUrl = product?.file_url || '';
    const previewUrl = product?.preview_url || '';

    /*
     * If an image preview was uploaded separately,
     * always prefer that for the visual preview.
     */
    if (previewUrl && isImageFile(previewUrl)) {
      return (
        <img
          src={previewUrl}
          alt={product.title}
          className="max-w-full max-h-[75vh] object-contain rounded"
        />
      );
    }

    if (isImageFile(fileUrl)) {
      return (
        <img
          src={fileUrl}
          alt={product.title}
          className="max-w-full max-h-[75vh] object-contain rounded"
        />
      );
    }

    if (isPdfFile(fileUrl)) {
      return (
        <iframe
          src={fileUrl}
          title={product.title}
          className="w-full h-[75vh] rounded bg-white"
        />
      );
    }

    if (isWordFile(fileUrl)) {
      return (
        <div className="flex flex-col items-center justify-center text-white py-20">
          <FiFileText size={64} className="mb-lg text-textLight" />

          <h3 className="text-lg font-semibold mb-sm">
            Word Document
          </h3>

          <p className="text-sm text-textLight mb-lg text-center max-w-md">
            This DOC/DOCX file cannot be previewed directly in the browser.
            You can open or download the original document.
          </p>

          <div className="flex gap-sm">
            <button
              onClick={() => handleOpenFile(product)}
              className="btn-secondary text-sm py-sm px-md text-white border-white hover:bg-secondaryGray flex items-center gap-xs"
            >
              <FiExternalLink size={16} />
              Open File
            </button>

            <button
              onClick={() => handleDownload(product)}
              className="btn-primary text-sm py-sm px-md flex items-center gap-xs"
            >
              <FiDownload size={16} />
              Download
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center text-white py-20">
        <FiFileText size={64} className="mb-lg text-textLight" />

        <h3 className="text-lg font-semibold mb-sm">
          File Preview
        </h3>

        <p className="text-sm text-textLight mb-lg">
          This file type cannot be previewed directly.
        </p>

        <button
          onClick={() => handleDownload(product)}
          className="btn-primary text-sm py-sm px-md flex items-center gap-xs"
        >
          <FiDownload size={16} />
          Download File
        </button>
      </div>
    );
  };

  return (
    <div>
      <div className="section-title">
        <div className="section-title__icon">
          <FiBox />
        </div>

        <div>
          <h2>Final Product Overview</h2>
          <p>
            Explore final deliverables including elevations and aerial top views.
          </p>
        </div>
      </div>

      {/* Tab Filter Navigation */}
      <div className="flex border-b border-borderLight mb-lg">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-md py-sm font-semibold text-sm transition-all duration-200 border-b-2 ${
            activeTab === 'all'
              ? 'border-primaryDark text-textPrimary'
              : 'border-transparent text-textMuted hover:text-textSecondary'
          }`}
        >
          All Schematics ({finalProducts.length})
        </button>
      </div>

      {/* Grid List */}
      {filteredProducts.length === 0 ? (
        <EmptyState
          message={`No blueprints uploaded yet matching filter "${activeTab}".`}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.product_id}
              product={product}
              onPreview={() => setPreviewProduct(product)}
            />
          ))}
        </div>
      )}

      {filteredProducts.length > 0 && (
        <div className="info-bar">
          <FiInfo />
          Select a deliverable to preview it, then download the original file when needed.
        </div>
      )}

      {/* Full Screen Preview Overlay */}
      {previewProduct && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-md">

          {/* Header */}
          <div className="w-full max-w-5xl flex justify-between items-center text-white mb-md">
            <div>
              <h3 className="font-bold text-base md:text-lg">
                {previewProduct.title}
              </h3>

              <p className="text-xs text-textLight uppercase tracking-wider">
                {previewProduct.product_type === 'top-view'
                  ? 'Site Topography Map'
                  : 'Elevation Blueprint'}
              </p>
            </div>

            <div className="flex items-center gap-sm">

              <button
                onClick={() => handleDownload(previewProduct)}
                className="btn-secondary text-sm py-xs px-sm text-white border-white hover:bg-secondaryGray flex items-center gap-xs"
              >
                <FiDownload size={16} />
                <span>Download</span>
              </button>

              <button
                onClick={() => setPreviewProduct(null)}
                className="p-sm bg-secondaryGray hover:bg-accentGray rounded-full text-white transition-colors duration-150"
              >
                <FiX size={20} />
              </button>

            </div>
          </div>

          {/* Preview */}
          <div className="w-full max-w-5xl max-h-[80vh] flex justify-center items-center bg-zinc-950 rounded-card overflow-hidden border border-secondaryGray p-xs">
            {renderPreview(previewProduct)}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalProduct;
